import GlassCard from '@/components/GlassCard';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LEVELS, LevelKey } from '../liveSessionsConfig';

export interface Professor {
    id: number;
    name: string;
    bio: string;
    avatar_url: string | null;
    order: number;
}

export interface SessionMaterial {
    id: number;
    title: string;
    file_url: string | null;
}

export interface LanguageLiveSession {
    id: number;
    title: string;
    level: LevelKey;
    video_url: string;
    vimeo_url: string;
    duration_minutes: number;
    professor: Professor | null;
    materials: SessionMaterial[];
}

const SPANISH_MONTHS: Record<string, number> = {
    enero: 0,
    febrero: 1,
    marzo: 2,
    abril: 3,
    mayo: 4,
    junio: 5,
    julio: 6,
    agosto: 7,
    septiembre: 8,
    octubre: 9,
    noviembre: 10,
    diciembre: 11,
};

export function extractDateFromTitle(title: string): Date | null {
    // DD-MM-YYYY (most common, e.g. "16-07-2026")
    let m = title.match(/(\d{1,2})-(\d{1,2})-(\d{4})/);
    if (m) return new Date(+m[3], +m[2] - 1, +m[1]);

    // Dotted format: MM.DD.YYYY or DD.MM.YYYY — disambiguate by which part exceeds 12
    m = title.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (m) {
        const a = +m[1],
            b = +m[2],
            y = +m[3];
        if (b > 12) return new Date(y, a - 1, b); // MM.DD.YYYY
        return new Date(y, b - 1, a); // DD.MM.YYYY
    }

    // DD.MonthName.YYYY in Spanish (e.g. "15.Julio.2026")
    m = title.match(
        /(\d{1,2})\.(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\.(\d{4})/i
    );
    if (m) return new Date(+m[3], SPANISH_MONTHS[m[2].toLowerCase()], +m[1]);

    return null;
}

interface LiveSessionCardProps {
    level: LevelKey;
    title: string;
    date?: Date | null;
    href?: string;
    onClick?: () => void;
    className?: string;
    children?: React.ReactNode;
}

const LiveSessionCard: React.FC<LiveSessionCardProps> = ({
    level: levelKey,
    title,
    date,
    href,
    onClick,
    className,
    children,
}) => {
    const { t } = useTranslation();
    const level = LEVELS[levelKey] ?? LEVELS.general;

    const cardAs = href ? 'link' : onClick ? 'button' : 'div';

    return (
        <GlassCard
            as={cardAs}
            href={href}
            onClick={onClick}
            className={clsx(
                'group flex flex-col overflow-hidden text-left shadow-lg',
                className
            )}
        >
            {/* Thin coloured accent bar */}
            <div
                className="h-1 w-full"
                style={{ backgroundColor: level.color }}
            />

            <div className="flex flex-1 flex-col gap-3 p-4">
                {/* Level badge + date */}
                <div className="flex items-center justify-between gap-2">
                    <span
                        className="rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
                        style={{ backgroundColor: level.color }}
                    >
                        {t(level.labelKey)}
                    </span>
                    {date ? (
                        <span className="text-xs text-gray-500 dark:text-neutral-400">
                            {date.toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                            })}
                        </span>
                    ) : null}
                </div>

                {/* Title */}
                <p className="line-clamp-3 flex-1 text-xs leading-snug font-semibold text-gray-900 dark:text-white">
                    {title}
                </p>

                {children}
            </div>
        </GlassCard>
    );
};

export default LiveSessionCard;
